import { useEffect, useMemo, useState } from "react";
import { api, fmtDate } from "../../lib/api";
import { toast } from "sonner";
import { Search, Film, Tv, Layers, Trash2 } from "lucide-react";

export const AdminSorties = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState("all");
  const [platform, setPlatform] = useState("all");
  const [q, setQ] = useState("");
  const [platforms, setPlatforms] = useState([]);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    api.get("/config").then((r) => setPlatforms(r.data.target_platforms));
  }, []);
  useEffect(() => {
    setLoading(true);
    const params = { with_poster: false, limit: 500 };
    if (kind !== "all") params.kind = kind;
    if (platform !== "all") params.platform = platform;
    api.get("/releases", { params }).then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, [kind, platform]);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const s = q.trim().toLowerCase();
    return items.filter((i) => i.title.toLowerCase().includes(s));
  }, [items, q]);

  const remove = async (r) => {
    const label = `${r.type} — ${r.title}`;
    if (!window.confirm(
      `Supprimer « ${label} » de la base ?\n\n` +
      `• Retiré de MongoDB (site public)\n` +
      `• Page Notion archivée (soft-delete)\n` +
      `• Votes associés purgés\n\n` +
      `Cette action est irréversible. Confirmer ?`
    )) return;
    const key = `${r.type}-${r.tmdb_id}`;
    setDeletingId(key);
    const t = toast.loading(`Suppression de ${r.title}…`);
    try {
      await api.delete(`/releases/${r.tmdb_id}`, { params: { type: r.type } });
      setItems((prev) => prev.filter((i) => !(i.tmdb_id === r.tmdb_id && i.type === r.type)));
      toast.success(`${label} supprimé`, { id: t });
    } catch (e) {
      toast.error("Suppression impossible", { id: t, description: e?.response?.data?.detail || e.message });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="text-[11px] uppercase tracking-[0.3em] text-[var(--red)]">Base TMDB</div>
        <h1 className="font-display text-4xl mt-2">Sorties synchronisées</h1>
        <p className="text-sm text-neutral-600 mt-1">
          {items.length} titre{items.length > 1 ? "s" : ""} en base — dédoublonnage automatique.
        </p>
      </div>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Chip active={kind === "all"} onClick={() => setKind("all")} icon={Layers} label="Tout" testId="adm-filter-all" />
          <Chip active={kind === "Film"} onClick={() => setKind("Film")} icon={Film} label="Films" testId="adm-filter-film" />
          <Chip active={kind === "Série"} onClick={() => setKind("Série")} icon={Tv} label="Séries" testId="adm-filter-serie" />
          <select
            data-testid="adm-filter-platform"
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
            className="text-xs px-3 py-1.5 rounded-full bg-white border border-[var(--line-strong)]"
          >
            <option value="all">Toutes plateformes</option>
            {platforms.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div className="relative w-full md:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
          <input
            data-testid="adm-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher…"
            className="w-full pl-9 pr-3 py-2 rounded-full bg-white border border-[var(--line-strong)] text-sm focus:outline-none focus:border-[var(--red)]"
          />
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="aspect-[2/3] rounded-md bg-neutral-100 animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {filtered.map((r) => {
            const key = `${r.type}-${r.tmdb_id}`;
            const isDeleting = deletingId === key;
            return (
              <div key={key} className="group relative bg-white border border-[var(--line)] rounded-md overflow-hidden card-lift">
                <div className="aspect-[2/3] bg-neutral-100">
                  {r.poster_url ? (
                    <img src={r.poster_url} alt={r.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="grid place-items-center h-full text-neutral-400 text-xs">—</div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => remove(r)}
                  disabled={isDeleting}
                  data-testid={`adm-delete-${r.tmdb_id}`}
                  title="Supprimer cette entrée"
                  className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 focus:opacity-100 disabled:opacity-100 transition-opacity h-8 w-8 grid place-items-center rounded-full bg-white/95 border border-[var(--line-strong)] text-neutral-500 hover:text-[var(--red)] hover:border-[var(--red)]"
                >
                  <Trash2 className={`h-3.5 w-3.5 ${isDeleting ? "animate-pulse" : ""}`} />
                </button>
                <div className="p-3">
                  <div className="text-sm font-medium truncate" title={r.title}>{r.title}</div>
                  <div className="text-[11px] text-neutral-500 mt-0.5 flex justify-between">
                    <span>{fmtDate(r.release_date)}</span>
                    <span>{r.type}</span>
                  </div>
                  <div className="text-[10px] text-neutral-400 mt-1 truncate">
                    {r.platforms.join(" · ")}
                  </div>
                  <div className="text-[10px] text-neutral-400 mt-0.5 font-mono">TMDB {r.tmdb_id}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const Chip = ({ active, onClick, icon: Icon, label, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs uppercase tracking-widest ${
      active ? "bg-[var(--red)] text-white border-[var(--red)]" : "bg-white text-neutral-700 border-[var(--line-strong)] hover:border-black"
    }`}
  >
    <Icon className="h-3.5 w-3.5" />{label}
  </button>
);
